import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { Offering } from "@/lib/types/database/offerings";
import {
  coreOfferingsFields,
  metadataOfferingsFields,
} from "../config";
import {
  offeringActions,
  offeringBulkActions,
  offeringSavedViews,
  offeringFeatures,
} from "../config";
import { ENHANCED_OFFERING_DETAIL_CONFIG } from "./OfferingDetailConfig";

/** Logical order for create offering form: identity → content/CTA → pricing → line items/totals → assets */
const CREATE_OFFERING_FIELD_ORDER: string[] = [
  "title",
  "description",
  "executive_summary",
  "cta_href",
  "cta_text",
  "pricing_model",
  "base_price",
  "pricing_display",
  "linked_project_slug",
  "tags",
  "staff_line_items",
  "hard_cost_line_items",
  "total_hours",
  "total_hard_costs",
  "total_cost",
  "image_url",
  "one_pager_pdf_url",
  "delivery_kit",
];

/**
 * Progressive-disclosure section layout for the Create Offering form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_OFFERING_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["title", "description"],
  },
  {
    id: "content",
    kind: "collapsible",
    title: "Content & CTA",
    summary: "Executive summary, CTA text/link",
    fieldNames: ["executive_summary", "cta_text", "cta_href"],
  },
  {
    id: "pricing",
    kind: "collapsible",
    title: "Pricing",
    summary: "Pricing model, base price, display",
    fieldNames: ["pricing_model", "base_price", "pricing_display"],
  },
  {
    id: "line-items",
    kind: "collapsible",
    title: "Line items & totals",
    summary: "Staff/hard cost line items and totals",
    fieldNames: [
      "staff_line_items",
      "hard_cost_line_items",
      "total_hours",
      "total_hard_costs",
      "total_cost",
    ],
  },
  {
    id: "classification",
    kind: "collapsible",
    summary: "Linked project and tags",
    fieldNames: ["linked_project_slug", "tags"],
  },
  {
    id: "assets",
    kind: "collapsible",
    title: "Assets & delivery",
    summary: "Image URL, one-pager PDF, delivery kit",
    fieldNames: ["image_url", "one_pager_pdf_url", "delivery_kit"],
  },
];

export const ENHANCED_OFFERINGS_CONFIG: SimpleTableConfig<Offering> = {
  entity: "offerings",
  displayName: "Offerings",
  // No status select-type field on offerings. Explicit opt-out — see SimpleTableConfigBase.statusFieldId.
  statusFieldId: null,
  defaultPageSize: 10,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,

  createFormFieldOrder: CREATE_OFFERING_FIELD_ORDER,
  createFormSections: CREATE_OFFERING_FORM_SECTIONS,

  /** Detail modal right rail: key fields only (line items & long JSON live in the main tabs / full edit). */
  inlineInfoPanelTitle: "Offering information",
  inlineInfoPanelFieldIds: [
    "title",
    "linked_project_slug",
    "tags",
    "pricing_model",
    "base_price",
    "pricing_display",
    "cta_text",
    "cta_href",
    "description",
    "executive_summary",
    "image_url",
    "one_pager_pdf_url",
    "created_at",
    "updated_at",
  ],

  /** Default table column order: offering → pricing model → price → project → tags */
  tableColumnOrder: [
    "title",
    "pricing_model",
    "pricing_display",
    "base_price",
    "linked_project_slug",
    "tags",
  ],

  /** Card view: SKU → price signal → segment */
  cardFaceFieldOrder: [
    "title",
    "pricing_model",
    "base_price",
    "pricing_display",
    "tags",
    "linked_project_slug",
  ],

  mobileDetailSections: [
    { id: "identity", label: "Identity" },
    { id: "content", label: "Content & CTA" },
    { id: "pricing", label: "Pricing & Line Items" },
    { id: "assets", label: "Assets & Delivery" },
    { id: "metadata", label: "Metadata" },
  ],

  fields: {
    ...coreOfferingsFields,
    ...metadataOfferingsFields,
  },

  actions: offeringActions,

  detailActions: ENHANCED_OFFERING_DETAIL_CONFIG.actions,

  bulkActions: offeringBulkActions,

  features: offeringFeatures,

  savedViews: offeringSavedViews,

  customFilterHooks: {},
};
